import { useCallback, useId, useMemo, useRef } from "react"

import { cn } from "@/shared/lib/utils"
import { composeEventHandlers } from "@/shared/lib/utils/compose-event-handlers"

import styles from "./Tooltip.module.css"
import { TooltipContext, useTooltipContext } from "./use-tooltip-context"

import type { PositionArea, PositionAreaSingle } from "./types"

type TooltipArea = Exclude<PositionArea, PositionAreaSingle>

interface RootProps extends React.PropsWithChildren {
  showDelay?: number
  hideDelay?: number
}

export const Root: React.FC<RootProps> = ({ showDelay = 500, hideDelay = 500, children }) => {
  const tooltipId = `tooltip-${useId()}`
  const tooltipRef = useRef<HTMLDivElement>(null)
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined)

  const showTooltip = useCallback(
    (delay: number = showDelay) => {
      clearTimeout(timeoutRef.current)

      timeoutRef.current = setTimeout(() => {
        if (!tooltipRef.current?.matches(":popover-open")) {
          tooltipRef.current?.showPopover()
        }
      }, delay)
    },
    [showDelay]
  )

  const hideTooltip = useCallback(
    (delay: number = hideDelay) => {
      clearTimeout(timeoutRef.current)

      timeoutRef.current = setTimeout(() => {
        if (tooltipRef.current?.matches(":popover-open")) {
          tooltipRef.current.hidePopover()
        }
      }, delay)
    },
    [hideDelay]
  )

  const contextValue = useMemo(
    () => ({
      tooltipId,
      tooltipRef,
      timeoutRef,
      showTooltip,
      hideTooltip
    }),
    [tooltipId, hideTooltip, showTooltip]
  )

  return <TooltipContext value={contextValue}>{children}</TooltipContext>
}

Root.displayName = "Tooltip.Root"

interface TriggerProps
  extends Omit<
    React.ComponentProps<"button">,
    "type" | "popoverTarget" | "aria-controls" | "aria-haspopup" | "aria-describedby"
  > {}

export const Trigger: React.FC<TriggerProps> = ({
  className,
  popoverTargetAction = "toggle",
  onBlur,
  onClick,
  onFocus,
  onMouseEnter,
  onMouseLeave,
  ...props
}) => {
  const { tooltipId, tooltipRef, timeoutRef, showTooltip, hideTooltip } = useTooltipContext()

  const onMouseEnterHandler: React.MouseEventHandler<HTMLButtonElement> = () => {
    showTooltip()
  }

  const onMouseLeaveHandler: React.MouseEventHandler<HTMLButtonElement> = () => {
    hideTooltip()
  }

  const onFocusHandler: React.FocusEventHandler<HTMLButtonElement> = () => {
    showTooltip(0)
  }

  const onBlurHandler: React.FocusEventHandler<HTMLButtonElement> = event => {
    onBlur?.(event)
    if (event.defaultPrevented) {
      return
    }

    clearTimeout(timeoutRef.current)
    if (tooltipRef.current?.matches(":popover-open")) {
      tooltipRef.current.hidePopover()
    }
  }

  const onClickHandler: React.MouseEventHandler<HTMLButtonElement> = event => {
    onClick?.(event)
    if (event.defaultPrevented) {
      return
    }

    clearTimeout(timeoutRef.current)
  }

  return (
    <button
      {...props}
      type="button"
      popoverTarget={tooltipId}
      popoverTargetAction={popoverTargetAction}
      aria-haspopup="true"
      aria-controls={tooltipId}
      aria-describedby={tooltipId}
      onMouseEnter={composeEventHandlers(onMouseEnter, onMouseEnterHandler)}
      onMouseLeave={composeEventHandlers(onMouseLeave, onMouseLeaveHandler)}
      onFocus={composeEventHandlers(onFocus, onFocusHandler)}
      onBlur={onBlurHandler}
      onClick={onClickHandler}
      data-anchor={`--${tooltipId}`}
      className={cn(styles.trigger, className)}
    />
  )
}

Trigger.displayName = "Tooltip.Trigger"

interface ContentProps extends Omit<React.ComponentPropsWithoutRef<"div">, "id" | "role" | "popover"> {
  tooltipArea?: TooltipArea
}

export const Content: React.FC<ContentProps> = ({
  className,
  //   tooltipArea = "span-self-block-end self-inline-start",
  tooltipArea = "center bottom",
  onMouseEnter,
  onMouseLeave,
  ...props
}) => {
  const { tooltipId, tooltipRef, showTooltip, hideTooltip } = useTooltipContext()

  const onMouseEnterHandler: React.MouseEventHandler<HTMLDivElement> = () => {
    showTooltip()
  }

  const onMouseLeaveHandler: React.MouseEventHandler<HTMLDivElement> = () => {
    hideTooltip()
  }

  return (
    <div
      {...props}
      id={tooltipId}
      role="tooltip"
      popover="auto"
      onMouseEnter={composeEventHandlers(onMouseEnter, onMouseEnterHandler)}
      onMouseLeave={composeEventHandlers(onMouseLeave, onMouseLeaveHandler)}
      data-anchor={`--${tooltipId}`}
      data-anchor-position-block={tooltipArea.split(" ")[0]}
      data-anchor-position-inline={tooltipArea.split(" ")[1]}
      ref={tooltipRef}
      className={cn(styles.tooltip, className)}
    />
  )
}
